import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLanguages } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCountriesDataView } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentCountries, { loadCurrentCountriesParameter } from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import { getCurrentShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import { CountryModel, LanguageModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    languageVariation = "languageVariation",
    countryVariation = "countryVariation",
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.languageVariation]: boolean;
        [fields.countryVariation]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const countriesDataView = getCountriesDataView(state, loadCurrentCountriesParameter);
    return {
        currentLanguage: state.context.session.language,
        languages: getLanguages(state),
        countriesDataView,
        currentShipTo: getCurrentShipToState(state).value,
        shouldLoadCountries: !countriesDataView.value && !countriesDataView.isLoading,
    };
}
const mapDispatchToProps = {
    loadCurrentCountries,
};

export interface TranslatableCmsContentStyles {
    wrapperShell: InjectableCss;
}
export const translatableCmsContentStyles: TranslatableCmsContentStyles = {
    wrapperShell: {
        css: css`
            position: relative;
            background: #e5e5e5;
            padding: 10px;
        `,
    },
};

const styles = translatableCmsContentStyles;

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

const TranslatableCmsContent: FC<Props> = props => {
    const { 
        currentLanguage,
        languages,
        countriesDataView,
        shouldLoadCountries,
        currentShipTo,
        id,
        fields,
        loadCurrentCountries,
    } = props;
    const { languageVariation, countryVariation } = fields;
    const shellContext = React.useContext(ShellContext);

    if ((languageVariation && !currentLanguage)) {
        return null;
    }

    if (shouldLoadCountries) {
        loadCurrentCountries();
    }

    const countries = countriesDataView.value;
    const currentCountry = currentShipTo?.country;

    const getZone = (language: LanguageModel | undefined, country: CountryModel | undefined, notLoggedIn?: boolean) => {
        return (
            <StyledWrapper {...styles.wrapperShell}>
                <Typography>{translate(`Content for ${
                    languageVariation && language ? `[language: ${language.languageCode.toUpperCase()}]` : ''
                } ${
                    countryVariation && country ? ` [country shipTo:${country.name}]` : notLoggedIn ? '[country:NOT_LOGGED_IN]' : ''
                }:`)}</Typography>
                <Zone contentId={id} zoneName={`Content${
                    languageVariation && language ? `${language.languageCode}` : ''
                }${
                    countryVariation && country ? `${country.abbreviation}` : ''
                }`} />
            </StyledWrapper>
        );
    };

    const getZonesForCountries = (language:LanguageModel | undefined, countries: CountryModel[] | undefined) => {
        return countries?.map((country, index) => {
            if (index === 0) {
                return (
                    <>
                        {getZone(language, undefined, true)}
                        {getZone(language, country)}
                    </>
                );
            }
            return getZone(language, country);
        })
    };

    return (
        <>
            {shellContext.isEditing ? (
                <>
                    <StyledWrapper {...styles.wrapperShell}>
                        <Typography>While in the CMS, you can see all CMS zones for every criteria, but on the website, only the one corresponding to the right language and/or country will be displayed.</Typography>
                    </StyledWrapper>
                    {(languageVariation || countryVariation) ? (
                        languageVariation
                            ? languages?.map(language => {
                                return countryVariation 
                                    ? getZonesForCountries(language, countries)
                                    : getZone(language, undefined)
                                }) 
                            : getZonesForCountries(undefined, countries)
                    ) : (
                        <StyledWrapper {...styles.wrapperShell}>
                            <Typography>{translate(`Content:`)}</Typography>
                            <Zone contentId={id} zoneName="Content" />
                        </StyledWrapper>
                    )}
                </>
            ) : (
                <Zone contentId={id} zoneName={`Content${languageVariation && currentLanguage ? `${currentLanguage.languageCode}` : ''}${countryVariation && currentCountry ? `${currentCountry.abbreviation}` : ''}`} />
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(TranslatableCmsContent),
    definition: {
        displayName: "Localized View",
        group: "Basic",
        fieldDefinitions: [
            {
                name: fields.languageVariation,
                displayName: "Language Variation",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.countryVariation,
                displayName: "Country Variation",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
